import { unsafeCSS } from "lit";
import MarkdownIt from "markdown-it";
import type { Token } from "markdown-it";
import { keymap } from "prosemirror-keymap";
import {
    MarkdownSerializer,
    MarkdownSerializerState,
} from "prosemirror-markdown";
import {
    Attrs,
    DOMSerializer,
    DOMParser as PMDOMParser,
    Node as ProseNode,
    Schema,
} from "prosemirror-model";
import { Command, Plugin } from "prosemirror-state";
import {
    addColumnAfter,
    addColumnBefore,
    addRowAfter,
    addRowBefore,
    columnResizing,
    deleteColumn,
    deleteRow,
    deleteTable,
    goToNextCell,
    isInTable,
    mergeCells,
    setCellAttr,
    splitCell,
    tableEditing,
    tableNodes,
} from "prosemirror-tables";
import { Transform } from "prosemirror-transform";

import { BaseExtension } from "@/wc/text-editor/lib/core/base-extension";
import type { MenuFunction, MenuItem } from "@/wc/text-editor/lib/core/menu";
import type {
    MarkdownSerializerConfig,
    NodeSerializerSpec,
    ParseSpecMap,
} from "@/wc/text-editor/lib/core/types";

import styles from "./styles.scss?inline";

type Alignment = "left" | "center" | "right";

// Transient node holding a raw <table> block until upgradeParsedDocument rebuilds it.
const HTML_TABLE = "html_table";

const tableNodeSpecs = tableNodes({
    tableGroup: "block",
    // inline-only, so a cell always round-trips to a single GFM cell
    cellContent: "inline*",
    cellAttributes: {
        alignment: {
            default: null,
            getFromDOM: (dom) => {
                const align = dom.style.textAlign;
                return align === "left" ||
                    align === "center" ||
                    align === "right"
                    ? align
                    : null;
            },
            setDOMAttr: (value, attrs) => {
                if (value) {
                    attrs.style = `text-align: ${value};${attrs.style ?? ""}`;
                }
            },
        },
    },
});

const alignmentFromToken = (token: Token): Attrs => {
    const match = /text-align:\s*(left|center|right)/.exec(
        token.attrGet("style") ?? ""
    );
    return { alignment: match ? match[1] : null };
};

const isMerged = (cell: ProseNode): boolean =>
    (cell.attrs.colspan ?? 1) > 1 || (cell.attrs.rowspan ?? 1) > 1;

const hasMergedCells = (table: ProseNode): boolean => {
    let merged = false;
    table.descendants((node) => {
        if (
            (node.type.name === "table_cell" ||
                node.type.name === "table_header") &&
            isMerged(node)
        ) {
            merged = true;
        }
        return !merged;
    });
    return merged;
};

const alignmentSeparator = (alignment: Alignment | null): string => {
    switch (alignment) {
        case "left":
            return ":---";
        case "center":
            return ":---:";
        case "right":
            return "---:";
        default:
            return "---";
    }
};

export const createTable = (
    schema: Schema,
    rows: number,
    cols: number
): ProseNode => {
    const headerRow = schema.nodes.table_row.create(
        null,
        Array.from(
            { length: cols },
            () => schema.nodes.table_header.createAndFill()!
        )
    );
    const bodyRows = Array.from({ length: rows - 1 }, () =>
        schema.nodes.table_row.create(
            null,
            Array.from(
                { length: cols },
                () => schema.nodes.table_cell.createAndFill()!
            )
        )
    );
    return schema.nodes.table.create(null, [headerRow, ...bodyRows]);
};

export const insertTable =
    (rows = 3, cols = 3): Command =>
    (state, dispatch) => {
        if (!state.schema.nodes.table || isInTable(state)) {
            return false;
        }
        if (dispatch) {
            const table = createTable(state.schema, rows, cols);
            dispatch(state.tr.replaceSelectionWith(table).scrollIntoView());
        }
        return true;
    };

const parseHtmlTable = (rawHtml: string, schema: Schema): ProseNode | null => {
    if (!/^\s*<table[\s>]/i.test(rawHtml)) {
        return null;
    }
    const dom = new DOMParser().parseFromString(rawHtml, "text/html");
    const tableElement = dom.querySelector("table");
    if (!tableElement) {
        return null;
    }
    const parsed = PMDOMParser.fromSchema(schema).parse(tableElement);
    let table: ProseNode | null = null;
    parsed.descendants((node) => {
        if (node.type.name === "table" && !table) {
            table = node;
        }
        return !table;
    });
    return table;
};

// A table at a document edge has no cursor position before/after it, so the user
// can't type there. Keep a paragraph on whichever edge a table occupies.
const tableEdgeParagraphPlugin = new Plugin({
    appendTransaction: (transactions, _oldState, newState) => {
        if (!transactions.some((tr) => tr.docChanged)) {
            return null;
        }
        const { doc, schema } = newState;
        const needsLeading = doc.firstChild?.type.name === "table";
        const needsTrailing = doc.lastChild?.type.name === "table";
        if (!needsLeading && !needsTrailing) {
            return null;
        }
        const tr = newState.tr;
        // Append before prepending so the trailing position stays valid.
        if (needsTrailing) {
            tr.insert(doc.content.size, schema.nodes.paragraph.create());
        }
        if (needsLeading) {
            tr.insert(0, schema.nodes.paragraph.create());
        }
        return tr;
    },
});

export class TableExtension extends BaseExtension {
    css = [unsafeCSS(styles)];

    modifySchema = (schema: Schema): Schema => {
        const nodes = schema.spec.nodes
            .append(tableNodeSpecs)
            .addToEnd(HTML_TABLE, {
                group: "block",
                content: "text*",
                code: true,
                // Fallback rendering if a carrier ever survives upgradeParsedDocument.
                toDOM: () => ["pre", { "data-html-table": "" }, 0],
            });
        return new Schema({ nodes, marks: schema.spec.marks });
    };

    // html:true captures <table> blocks (merged cells); html_inline stays off so
    // stray inline <...> remains literal text.
    modifyMarkdownIt = (base: MarkdownIt): MarkdownIt =>
        base.enable("table").set({ html: true }).disable("html_inline");

    modifyParser = (parser: ParseSpecMap): ParseSpecMap => ({
        ...parser,
        table: { block: "table" },
        thead: { ignore: true },
        tbody: { ignore: true },
        tr: { block: "table_row" },
        th: { block: "table_header", getAttrs: alignmentFromToken },
        td: { block: "table_cell", getAttrs: alignmentFromToken },
        html_block: { block: HTML_TABLE, noCloseToken: true },
    });

    upgradeParsedDocument = async (
        doc: ProseNode,
        schema: Schema
    ): Promise<ProseNode> => {
        const carriers: { node: ProseNode; pos: number }[] = [];
        doc.descendants((node, pos) => {
            if (node.type.name === HTML_TABLE) {
                carriers.push({ node, pos });
                return false;
            }
            return true;
        });
        if (carriers.length === 0) {
            return doc;
        }

        const tr = new Transform(doc);
        // Replace bottom-up so earlier positions stay valid.
        for (const { node, pos } of carriers.reverse()) {
            const raw = node.textContent;
            const replacement =
                parseHtmlTable(raw, schema) ??
                schema.nodes.paragraph.create(
                    null,
                    raw ? schema.text(raw) : null
                );
            tr.replaceWith(pos, pos + node.nodeSize, replacement);
        }
        return tr.doc;
    };

    modifySerializer = (
        config: MarkdownSerializerConfig
    ): MarkdownSerializerConfig => {
        // Reuse the configured mark serializers to render cell content as one line.
        const cellMarkdown = (cell: ProseNode): string => {
            const schema = cell.type.schema;
            const wrapper = schema.nodes.doc.create(null, [
                schema.nodes.paragraph.create(null, cell.content),
            ]);
            const markdown = new MarkdownSerializer(
                config.nodes,
                config.marks,
                config.options
            ).serialize(wrapper);
            return markdown.replace(/\n+/g, " ").trim().replace(/\|/g, "\\|");
        };

        const serializeGfm = (
            state: MarkdownSerializerState,
            table: ProseNode
        ): void => {
            const rows: ProseNode[] = [];
            table.forEach((row) => rows.push(row));
            if (rows.length === 0) {
                state.closeBlock(table);
                return;
            }
            const renderRow = (row: ProseNode): string => {
                const cells: string[] = [];
                row.forEach((cell) => cells.push(cellMarkdown(cell)));
                return `| ${cells.join(" | ")} |`;
            };
            const separators: string[] = [];
            rows[0].forEach((cell) =>
                separators.push(alignmentSeparator(cell.attrs.alignment))
            );

            state.write(renderRow(rows[0]));
            state.ensureNewLine();
            state.write(`| ${separators.join(" | ")} |`);
            state.ensureNewLine();
            for (let i = 1; i < rows.length; i++) {
                state.write(renderRow(rows[i]));
                state.ensureNewLine();
            }
            state.closeBlock(table);
        };

        // Merged cells have no GFM form; emit an HTML <table> via the node's toDOM.
        const serializeHtml = (
            state: MarkdownSerializerState,
            table: ProseNode
        ): void => {
            const dom = DOMSerializer.fromSchema(
                table.type.schema
            ).serializeNode(table) as HTMLElement;
            state.write(dom.outerHTML);
            state.closeBlock(table);
        };

        const serializeTable: NodeSerializerSpec = (state, node) => {
            if (hasMergedCells(node)) {
                serializeHtml(state, node);
            } else {
                serializeGfm(state, node);
            }
        };

        const noop: NodeSerializerSpec = () => {};

        return {
            ...config,
            nodes: {
                ...config.nodes,
                table: serializeTable,
                table_row: noop,
                table_cell: noop,
                table_header: noop,
                [HTML_TABLE]: (state, node) => {
                    state.write(node.textContent);
                    state.closeBlock(node);
                },
            },
        };
    };

    plugins = () => [
        columnResizing(),
        tableEditing(),
        tableEdgeParagraphPlugin,
        keymap({
            "Tab": goToNextCell(1),
            "Shift-Tab": goToNextCell(-1),
        }),
    ];

    menu = (currentMenu: MenuFunction): MenuFunction => {
        type DropdownItem = NonNullable<MenuItem["items"]>[number];

        const op = (
            name: string,
            label: string,
            icon: string,
            command: Command
        ): DropdownItem => ({
            type: "toggle",
            name,
            label,
            icon: { name: icon },
            run: (state, dispatch, view) => {
                command(state, dispatch, view);
                view.focus();
            },
            disabled: (state) => !command(state),
        });

        const alignItem = (alignment: Alignment, icon: string): DropdownItem =>
            op(
                `table-align-${alignment}`,
                `Align ${alignment}`,
                icon,
                setCellAttr("alignment", alignment)
            );

        const inTableItems: DropdownItem[] = [
            op(
                "table-row-before",
                "Insert row above",
                "table-row-above",
                addRowBefore
            ),
            op(
                "table-row-after",
                "Insert row below",
                "table-row-below",
                addRowAfter
            ),
            op(
                "table-col-before",
                "Insert column left",
                "table-col-left",
                addColumnBefore
            ),
            op(
                "table-col-after",
                "Insert column right",
                "table-col-right",
                addColumnAfter
            ),
            op("table-merge", "Merge cells", "table-merge", mergeCells),
            op("table-split", "Split cell", "table-split", splitCell),
            op("table-delete-row", "Delete row", "trash", deleteRow),
            op("table-delete-col", "Delete column", "trash", deleteColumn),
            op("table-delete", "Delete table", "trash", deleteTable),
            alignItem("left", "justify-left"),
            alignItem("center", "justify"),
            alignItem("right", "justify-right"),
        ];

        const insertItem = (count: number): DropdownItem => ({
            type: "toggle",
            name: `table-insert-${count}-columns`,
            label: `${count} columns`,
            icon: { name: "plus-circle" },
            run: (state, dispatch, view) => {
                insertTable(2, count)(state, dispatch, view);
                view.focus();
            },
            disabled: (state) => isInTable(state),
        });

        return (view) => [
            ...currentMenu(view),
            {
                type: "dropdown",
                name: "insert-table",
                label: "Insert Table",
                icon: { name: "plus-square" },
                caret: true,
                showInInlineMenu: true,
                group: "base",
                order: 28,
                disabled: (state) => isInTable(state),
                items: [
                    insertItem(1),
                    insertItem(2),
                    insertItem(3),
                    insertItem(4),
                    insertItem(5),
                ],
            },
            {
                type: "dropdown",
                name: "in-table",
                label: "Table settings",
                icon: { name: "table" },
                caret: true,
                group: "base",
                order: 28,
                active: (state) => isInTable(state),
                disabled: (state) => !isInTable(state),
                items: inTableItems,
            },
        ];
    };
}
